/**
 * IncidentCuration — the report-build "curate" surface.
 *
 * Lists the period's analyst-marked candidates (pre-selected) plus the auto
 * top-5 suggestions, lets the analyst pick + reorder the featured set, draft a
 * per-incident "why this mattered" narration via the writing agent, and
 * edit/approve it before generation. Emits the ordered FeaturedIncidentInput[]
 * up to the parent via onChange.
 *
 * Order of `selected` IS the display order (index 0 = first card). Empty
 * selection → the report falls back to its auto top-5.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2,
  AlertTriangle,
  Plus,
  X,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Star,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { SeverityBadge } from '../ui/SeverityBadge';
import type { Severity } from '../../data/types';
import {
  getReportCandidates,
  draftIncidentNarration,
  type ReportCandidate,
  type FeaturedIncidentInput,
} from '../../services/reportGeneration';

interface CuratedItem {
  incidentId: string;
  title: string;
  severity: string;
  incidentFamily: string | null;
  riskScore: number | null;
  whyItMattered: string;
  narrativeText: string; // one paragraph per line
  lessonsText: string; // one lesson per line
  drafting: boolean;
  draftError: string | null;
}

interface IncidentCurationProps {
  tenantId: string;
  /** ISO datetime (UTC) bounds matching the report period. */
  periodStart: string;
  periodEnd: string;
  /** Emits the ordered, narration-approved featured set whenever it changes. */
  onChange: (featured: FeaturedIncidentInput[]) => void;
}

function candidateToItem(c: ReportCandidate): CuratedItem {
  return {
    incidentId: c.incidentId,
    title: c.title,
    severity: c.severity,
    incidentFamily: c.incidentFamily,
    riskScore: c.riskScore,
    whyItMattered: '',
    narrativeText: '',
    lessonsText: '',
    drafting: false,
    draftError: null,
  };
}

function itemToFeatured(it: CuratedItem): FeaturedIncidentInput {
  const narrative = it.narrativeText.split('\n').map((s) => s.trim()).filter(Boolean);
  const lessons = it.lessonsText.split('\n').map((s) => s.trim()).filter(Boolean);
  const out: FeaturedIncidentInput = { incidentId: it.incidentId };
  if (it.title.trim()) out.title = it.title.trim();
  if (it.whyItMattered.trim()) out.whyItMattered = it.whyItMattered.trim();
  if (narrative.length) out.narrative = narrative;
  if (lessons.length) out.lessons = lessons;
  return out;
}

export function IncidentCuration({
  tenantId,
  periodStart,
  periodEnd,
  onChange,
}: IncidentCurationProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [pool, setPool] = useState<ReportCandidate[]>([]);
  const [selected, setSelected] = useState<CuratedItem[]>([]);

  // Keep the latest onChange without retriggering the emit effect.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Emit the featured set upward whenever the selection changes.
  useEffect(() => {
    onChangeRef.current(selected.map(itemToFeatured));
  }, [selected]);

  const load = useCallback(async () => {
    if (!tenantId || !periodStart || !periodEnd) return;
    setLoading(true);
    setError(null);
    const res = await getReportCandidates(tenantId, periodStart, periodEnd);
    if (res.success) {
      // No prefill — analysts must actively pick. Both marked candidates and
      // the auto-suggestions go into the pool; nothing is pre-selected, so the
      // report only ever features what the analyst deliberately chooses.
      const candidateIds = new Set(res.data.candidates.map((c) => c.incidentId));
      setSelected([]);
      setPool([
        ...res.data.candidates,
        ...res.data.suggestions.filter((s) => !candidateIds.has(s.incidentId)),
      ]);
      setLoaded(true);
    } else {
      setError(res.error ?? 'Failed to load candidates');
    }
    setLoading(false);
  }, [tenantId, periodStart, periodEnd]);

  // Reset when the tenant/period changes; analyst loads explicitly.
  useEffect(() => {
    setLoaded(false);
    setSelected([]);
    setPool([]);
    setError(null);
  }, [tenantId, periodStart, periodEnd]);

  function addFromPool(c: ReportCandidate) {
    setSelected((prev) => [...prev, candidateToItem(c)]);
    setPool((prev) => prev.filter((p) => p.incidentId !== c.incidentId));
  }

  function removeSelected(incidentId: string) {
    setSelected((prev) => prev.filter((it) => it.incidentId !== incidentId));
  }

  function move(index: number, dir: -1 | 1) {
    setSelected((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function patchItem(incidentId: string, patch: Partial<CuratedItem>) {
    setSelected((prev) =>
      prev.map((it) => (it.incidentId === incidentId ? { ...it, ...patch } : it)),
    );
  }

  async function draft(incidentId: string) {
    patchItem(incidentId, { drafting: true, draftError: null });
    const res = await draftIncidentNarration(tenantId, incidentId);
    if (res.success) {
      const n = res.data.narration;
      patchItem(incidentId, {
        whyItMattered: n.whyItMattered ?? '',
        narrativeText: (n.narrative ?? []).join('\n'),
        lessonsText: (n.lessons ?? []).join('\n'),
        drafting: false,
      });
    } else {
      patchItem(incidentId, { drafting: false, draftError: res.error ?? 'Draft failed' });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-text-primary">Featured incidents</h3>
          <p className="text-xs text-text-secondary mt-0.5">
            Pick the incidents to feature from the suggestions below (★ = analyst-marked).
            At least one is required — the report features only what you choose.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={load} disabled={loading || !tenantId}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {loaded ? 'Reload' : 'Load incidents'}
        </Button>
      </div>

      {error && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loaded && (
        <>
          {/* Selected / ordered list */}
          {selected.length === 0 ? (
            <div className="text-sm text-text-secondary border border-dashed border-border rounded-lg p-4">
              No incidents selected yet — pick from the suggestions below. At least one is required to generate.
            </div>
          ) : (
            <ol className="space-y-3">
              {selected.map((it, idx) => (
                <li
                  key={it.incidentId}
                  className="border border-border rounded-lg bg-bg-secondary p-3 space-y-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-1 pt-0.5">
                      <span className="text-xs font-mono text-text-secondary w-5 text-center">
                        {idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="text-text-secondary hover:text-text-primary disabled:opacity-30"
                        aria-label="Move up"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, 1)}
                        disabled={idx === selected.length - 1}
                        className="text-text-secondary hover:text-text-primary disabled:opacity-30"
                        aria-label="Move down"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <SeverityBadge severity={(it.severity as Severity) ?? 'informational'} />
                        {it.incidentFamily && (
                          <span className="text-xs text-text-secondary">{it.incidentFamily}</span>
                        )}
                        {it.riskScore != null && (
                          <span className="text-xs font-mono text-text-secondary">
                            risk {it.riskScore}
                          </span>
                        )}
                        <span className="text-[11px] font-mono text-text-secondary truncate">
                          {it.incidentId}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={it.title}
                        onChange={(e) => patchItem(it.incidentId, { title: e.target.value })}
                        className="w-full mt-2 px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm font-medium"
                        placeholder="Card title"
                      />
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => draft(it.incidentId)}
                        disabled={it.drafting}
                        title="Draft 'why this mattered' with the writing agent"
                      >
                        {it.drafting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        Draft
                      </Button>
                      <button
                        type="button"
                        onClick={() => removeSelected(it.incidentId)}
                        className="text-text-secondary hover:text-red-600 p-1"
                        aria-label="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {it.draftError && (
                    <div className="text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {it.draftError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-2 pl-8">
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">
                        Why this mattered
                      </label>
                      <input
                        type="text"
                        value={it.whyItMattered}
                        onChange={(e) => patchItem(it.incidentId, { whyItMattered: e.target.value })}
                        className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm"
                        placeholder="One line — drafted by the agent, edit freely"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">
                        Narrative <span className="font-normal">(one paragraph per line)</span>
                      </label>
                      <textarea
                        rows={3}
                        value={it.narrativeText}
                        onChange={(e) => patchItem(it.incidentId, { narrativeText: e.target.value })}
                        className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm"
                        placeholder="What happened and how the SOC handled it"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">
                        Lessons <span className="font-normal">(one per line)</span>
                      </label>
                      <textarea
                        rows={2}
                        value={it.lessonsText}
                        onChange={(e) => patchItem(it.incidentId, { lessonsText: e.target.value })}
                        className="w-full px-2 py-1 rounded bg-bg-primary border border-border text-text-primary text-sm"
                        placeholder="Takeaways / hardening points"
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {/* Pool of suggestions to add */}
          {pool.length > 0 && (
            <div className="border border-border rounded-lg p-3">
              <p className="text-xs font-medium text-text-secondary mb-2">
                Suggested incidents — select to feature (★ = analyst-marked)
              </p>
              <ul className="space-y-1.5">
                {pool.map((c) => (
                  <li
                    key={c.incidentId}
                    className="flex items-center gap-2 text-sm"
                  >
                    <button
                      type="button"
                      onClick={() => addFromPool(c)}
                      className="text-brand-blue hover:opacity-80 p-1"
                      aria-label="Add to featured"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <SeverityBadge
                      severity={(c.severity as Severity) ?? 'informational'}
                      showPulse={false}
                    />
                    <span className="text-text-primary truncate flex-1">{c.title}</span>
                    {c.reportCandidate && (
                      <Star className="w-3.5 h-3.5 text-accent-yellow" aria-label="Marked" />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
